import Api from "../api/api";
import {useEffect,useState,useRef} from "react";
import { DateGroupingTypeEnum, reportDatePresetByLabel } from "./reportsEnums";
import {dateRange, toDateString} from "../../utilities/dateUtilities";
import {saveAs} from 'file-saver';
import Loader from "../shared/loader";
import {EmptyState} from "../dashboard/emptyState";
import {ReportsCharts} from "./reportsCharts";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import Enums from "../../utilities/enum";
import {FiltersModalTrigger} from "../shared/filtersModalTrigger";
import {AppliedFilterDisplay} from "../shared/appliedFilterDisplay";
import {BreachesReportFiltersModal} from "./filters-modals/breachesReportFiltersModal";
import {Link} from "react-router-dom/cjs/react-router-dom.min";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
import { BreachesScheduleModal } from "./schedule-modals/breachesScheduleModal";

export default function BreachesReport({sites, organisationId, organisationName, organisationLogo, token}) {

    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSite, setSelectedSite] = useState(!arrayUtilities.isNullOrEmpty(sites) ? sites[0].id : null);
    const [selectedDateGrouping, setSelectedDateGrouping] = useState(0);
    const [chartStyle, setChartStyle] = useState("bar");
    const [chartData, setChartData] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(0);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;
    const pdfRef = useRef()

    const pdfSiteName = (siteId) => {
        if (!stringHelpers.isNullOrEmpty(siteId)) {
            const site = sites.find(item => item.id === siteId)
            if (site !== undefined) {
                return site.name
            }
        }
        return "All"
    }

    const pdfDateGroupingName = (dateGroupingValue) => {
        const dateGroupingName = DateGroupingTypeEnum[dateGroupingValue]
        return !stringHelpers.isNullOrEmpty(dateGroupingName) ? dateGroupingName : "All"
    }

    const pdfStatusName = (statusValue) => {
        const statusName = Enums.BreachStatus[statusValue]
        return !stringHelpers.isNullOrEmpty(statusName) ? statusName : "All"
    }

    const fetchBreachesReport = (from, to, dateGrouping, status, siteId) => {
        setIsLoading(true);
        Api.getBreachesReport(
            reportData => {
                setReportsData(reportData);
                setIsLoading(false)
            },
            () => setIsLoading(false),
            from, to, 0, Number(dateGrouping), status, siteId, organisationId, token
        )
    }

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        const defaultSiteId = !arrayUtilities.isNullOrEmpty(sites) ? sites[0].id : null;
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setSelectedSite(defaultSiteId);
        setSelectedStatus(0)
        setSelectedDateGrouping(0);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Site": !arrayUtilities.isNullOrEmpty(sites) ? sites[0].name : null,
            "Date Grouping": DateGroupingTypeEnum[0],
            "Status": Enums.BreachStatus[0]
        });
        setShowFilterPills(true);
        fetchBreachesReport(defaultRange.firstDate, defaultRange.lastDate, 0, 0, defaultSiteId);
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        siteId: selectedSite,
        dateRangeType: selectedDateGrouping,
        breachStatus: selectedStatus,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchBreachesReport(fromDate, toDate, selectedDateGrouping, selectedStatus, selectedSite);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Site": sites?.find(item => item.id === selectedSite)?.name ?? null,
            "Date Grouping": DateGroupingTypeEnum[selectedDateGrouping],
            "Status": Enums.BreachStatus[selectedStatus]
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            setChartData(reportsData.map(item => ({
                "label": item.label,
                "value": item.value,
            })));
        }
    }, [reportsData, chartStyle])

    const handleDownload = (outputFormat) => {
        Api.getBreachesReport(
            (blob, filename) => { saveAs(blob, filename) },
            () => { setIsLoading(false) },
            fromDate,
            toDate,
            outputFormat,
            Number(selectedDateGrouping),
            selectedStatus,
            selectedSite,
            organisationId,
            token,
        )
    }

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            Breaches
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                                <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                            </div>
                                        <button
                                            onClick={ () => handleDownload(1) }
                                            className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                        >
                                            Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Breaches`) }
                                            className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                        >
                                            Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                        </button>
                                        <button
                                            onClick={ () => setShowScheduleExportModal(true) }
                                            className="btn btn-sm btn-secondary nowrap mr-2"
                                        >
                                            Schedule Report<i className="ml-2 far fa-clock"></i>
                                        </button>
                                        <Link
                                            className="btn btn-falcon-default btn-sm mr-3"
                                            type="button"
                                            to={ reportsLandingUrl }>
                                            <i className="fas fa-reply"></i> Back
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    return !stringHelpers.isNullOrEmpty(value)
                                                    && <div className="mr-2" key={ label }>
                                                        <AppliedFilterDisplay
                                                            label={ label }
                                                            isResettable={ true }
                                                            onTextClick={ () => setShowFiltersModal(true) }
                                                            onCloseClick={ closedLabel => {
                                                                const defaultRange = dateRange("lastMonth")
                                                                const defaultSiteId = !arrayUtilities.isNullOrEmpty(sites) ? sites[0].id : null
                                                                switch (closedLabel) {
                                                                    case "Date Range":
                                                                        setFromDate(defaultRange.firstDate)
                                                                        setToDate(defaultRange.lastDate)
                                                                        setDatePreset(reportDatePresetByLabel["Last month"])
                                                                        fetchBreachesReport(defaultRange.firstDate, defaultRange.lastDate, selectedDateGrouping, selectedStatus, selectedSite)
                                                                        break
                                                                    case "Site":
                                                                        setSelectedSite(defaultSiteId)
                                                                        fetchBreachesReport(fromDate, toDate, selectedDateGrouping, selectedStatus, defaultSiteId)
                                                                        break
                                                                    case "Date Grouping":
                                                                        setSelectedDateGrouping(0)
                                                                        fetchBreachesReport(fromDate, toDate, 0, selectedStatus, selectedSite)
                                                                        break
                                                                    case "Status":
                                                                        setSelectedStatus(0)
                                                                        fetchBreachesReport(fromDate, toDate, selectedDateGrouping, 0, selectedSite)
                                                                        break
                                                                    default:
                                                                        break
                                                                }
                                                                setAppliedFilters(prevState => {
                                                                    const updatedFilters = { ...prevState }
                                                                    if (closedLabel === "Date Range") {
                                                                        updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
                                                                    } else if (closedLabel === "Site") {
                                                                        updatedFilters["Site"] = !arrayUtilities.isNullOrEmpty(sites) ? sites[0].name : null
                                                                    } else if (closedLabel === "Date Grouping") {
                                                                        updatedFilters["Date Grouping"] = DateGroupingTypeEnum[0]
                                                                    } else if (closedLabel === "Status") {
                                                                        updatedFilters["Status"] = Enums.BreachStatus[0]
                                                                    }
                                                                    const hasRemainingFilters = Object.values(updatedFilters).some(value => !stringHelpers.isNullOrEmpty(value))
                                                                    return hasRemainingFilters ? updatedFilters : null
                                                                })
                                                            } }
                                                            value={ value }
                                                        />
                                                    </div>
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Breaches">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Site</span>
                                            <span className="print-only-filter-value">
                                                { pdfSiteName(selectedSite) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Grouping</span>
                                            <span className="print-only-filter-value">
                                                { pdfDateGroupingName(selectedDateGrouping) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Status</span>
                                            <span className="print-only-filter-value">
                                                { pdfStatusName(selectedStatus) }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                    {
                                        isLoading
                                            ? <Loader />
                                            : <>
                                                {
                                                    !arrayUtilities.isNullOrEmpty(reportsData) ? (<div className="mt-3 d-flex flex-column">
                                                        {chartData && <>
                                                            <div className="pl-6 mb-3 print-hide">
                                                                <button
                                                                    className={`btn btn-${chartStyle === "bar" ? "dark" : "secondary"} btn-sm mr-1 nowrap`}
                                                                    onClick={() => setChartStyle("bar")}><i className="fas fa-chart-bar mr-2"></i>Bar Graph
                                                                </button>
                                                                <button
                                                                    className={`btn btn-${chartStyle === "line" ? "dark" : "secondary"} btn-sm mr-1 ml-1 nowrap`}
                                                                    onClick={() => setChartStyle("line")}><i className="fas fa-chart-line mr-2"></i>Line Graph
                                                                </button>
                                                            </div>
                                                            <ReportsCharts data={chartData} chartStyle={chartStyle}/>
                                                        </>}
                                                        <div className="card-body bg-white border-top p-0">
                                                            <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                <thead className="bg-200 text-900">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th className="text-right">Number of Vehicles</th>
                                                                </tr>
                                                                </thead>

                                                                <tbody>
                                                                {!arrayUtilities.isNullOrEmpty(reportsData) && reportsData.map((item,index) => <tr key={index}>
                                                                    <td>{item.label}</td>
                                                                    <td className="text-right">{stringHelpers.formatDecimal(item.value, 0)}</td>
                                                                </tr>)}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>) : <EmptyState message="No Data available for the selected filters"/>}
                                            </>}
                                </div>
                                {
                                    showScheduleExportModal &&
                                    <BreachesScheduleModal
                                        sites={ sites }
                                        organisationId={ organisationId }
                                        token={ token }
                                        initialParameters={ scheduleInitialParameters }
                                        onCancel={ closeScheduleExportModal }
                                        onSaved={ closeScheduleExportModal }
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <BreachesReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setSelectedSite(draftFilters.selectedSite)
                        setSelectedDateGrouping(draftFilters.selectedDateGrouping)
                        setSelectedStatus(draftFilters.selectedStatus)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchBreachesReport(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedDateGrouping, draftFilters.selectedStatus, draftFilters.selectedSite)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialSelectedSite={ selectedSite }
                    initialSelectedDateGrouping={ selectedDateGrouping }
                    initialSelectedStatus={ selectedStatus }
                    initialAppliedFilters={ appliedFilters }
                    sites={ sites }
                    key="breaches-report-filters-modal"
                />
            }
        </>
    )
}
